//AutoCompleteAdvancedMUI.tsx =================================================
import "../Util/Prototypes";

import * as AdvancedSearch from "../Search/AdvancedSearch";
import * as Settings from "../Settings";
import * as Util from "../Util/Util";
import * as Util_App from "../Util/Util_App";

import { Autocomplete, Popper, TextField } from "@mui/material";

import React from "react";

import Query from "@arcgis/core/rest/support/Query";
import * as QueryTask from "@arcgis/core/rest/query";

const refreshRate: number = 400; // Milliseconds.

let startsWith: boolean;
let prevId: number = 0;
let currentValue: iOption = { label: "", id: -1 };
let enableEnterKey: boolean = false;
let startValue: string = "";

interface iProps {
   initialValue?: string;  //Initial label value.
   width?: number;         //Must be an integer.
   backgroundColor?: string;
   highlightColor?: string;
   toMixedCase?: boolean;  //Convert to proper case.
   tooltip?: string;       //Overall tooltip.
   focusMe?: boolean;      //Focus on startup.
   ajaxData?: any;
   children?: any;         //Placeholder hint text.
   onValueChanged?(label: string): void; //Textbox label changed function.
   onSelected(value: any, exactSearch: boolean): void;  //Selected value changed function.
}

interface iWord {
   value: string; // Word text.
   type: string;  // "Normal", "Bold", "LayerName", "AliasName".
}

interface iOption {
   label: string;     // Raw label string.
   id: number;        // 0 to 9 if history or 1 to 10 if not.
   words?: iWord[];   // Words that make up the suggestion for rendering.
   outValue?: string; // LayerName|FieldName|Value
}

//Render function.
export function Render(props: iProps) {
   //Util.errMsg("2) Top of AutoComplete Render Initial Value: " + props.initialValue);

   const [value, setValue] = React.useState({ label: "", id: -1 });
   const [inputValue, setInputValue] = React.useState("");
   const [options, setOptions] = React.useState([]);

   //Component did mount. Only fires when AdvancedSearch mounts.
   React.useEffect(() => {
      //Util.errMsg("Did Mount Initial Value: " + props.initialValue);

      //Restore previous search value.
      if (props.initialValue) {
         //Util.errMsg("Restoring previous value: " + currentValue.label + " " + currentValue.id);
         setValue({ label: props.initialValue, id: -1 });
         startValue = props.initialValue;
      }
      setTimeout(function () { enableEnterKey = true; }, 300);

      //Component will unmount.
      return () => {
      };
   }, []);

   //Component did update. Only fires when AdvancedSearch mounts or autoComplete refreshes.
   React.useEffect(() => {
      //Util.errMsg("Did Update Initial Value: " + props.initialValue);

      //Send label value back to calling component.
      props.onValueChanged(inputValue);

      //Clear the popup options menu when input textbox is blank.
      if (!inputValue) {
         setTimeout(function () { setOptions([]); }, 400);
         return undefined;
      }

      //Get suggestion(s) from server.
      prevId++;

      if (inputValue) {
         //Util.errMsg("*** Getting Server Data: " + inputValue);
         setTimeout(function () { getServerData(inputValue.trim(), prevId); }, refreshRate);
      }

   }, [value, inputValue]);

   //Get server data.
   function getServerData(searchText: string, id: number) {

      if (id === prevId) {

         //See if search string starts with a number.
         const firstChar: string = searchText.charAt(0);
         const lastChar: string = searchText.charAt(searchText.length - 1);
         const startsWithNumber: boolean = firstChar.IsNumeric();
         const endsWithNumber: boolean = lastChar.IsNumeric();

         //Set contains all the words or starts-with.
         startsWith = false;
         if ((startsWithNumber && endsWithNumber) || (props.ajaxData.fieldName.toLowerCase() === Settings.pin_fieldName.toLowerCase()) || (searchText.length < 4)) {
            startsWith = true;
         }

         //Get sql where string.
         const fieldName = props.ajaxData.fieldName as string;
         let operator = props.ajaxData.operator as string;
         const dataType = props.ajaxData.dataType as string;

         if (dataType === "string") {
            if (searchText === "?") {
               operator = "<>";
            }

         } else if (dataType === "date") {
            if (!searchText.IsValidDate()) {
               operator = "<>";
            }

         } else { //Numeric.
            if (!searchText.IsNumeric()) {
               operator = "<>";
            }
         }

         const sql: string = Util_App.buildQueryClause(fieldName, operator, searchText, dataType);

         //Initialize query.
         const query = new Query({
            returnGeometry: false,
            outFields: [fieldName],
            returnDistinctValues: true,
            orderByFields: [fieldName],
            where: sql,
            start: 0,
            num: 10,
         });

         //Execute query.
         const laySpec: Settings.iLayer = Util_App.getLayerSpec(AdvancedSearch.layerName);
         if (!laySpec) {
            Util.errMsg("Layer setting not found for " + AdvancedSearch.layerName);
            return;
         }
         const url: string = Util_App.getLayerURL(laySpec);
         QueryTask.executeQueryJSON(url, query).then(queryTask_success).catch(queryTask_failed);
      }
   }

   //Query task completed.
   function queryTask_success(response: __esri.FeatureSet) {
      //Util.logMsg("Response returned from Server", response);
      let features = response.features;
      let suggest: string;
      let words: iWord[];
      let outValue: string;
      const newOptions: iOption[] = [];

      const fieldName = props.ajaxData.fieldName as string;

      //Process all records.
      let ii: number = 0;

      for (const feature of features) {
         ii++;

         let anyValue = Util_App.getFieldValueFAT(fieldName, feature, AdvancedSearch.layerName);
         if (anyValue === null) {
            anyValue = "";
         }

         //Decode date fields.
         if (props.ajaxData.dataType === "date" && anyValue !== "") {
            anyValue = new Date(anyValue);
            anyValue = Util.formatTextValue(anyValue, "ShortDate");
         }

         if (fieldName.toLowerCase() === "shape_length" ||
            fieldName.toLowerCase() === "shape__length" ||
            fieldName.toLowerCase() === "shape.stlength()") {
            anyValue = Util.round(anyValue, 2);
         }

         if (fieldName.toLowerCase() === "shape_area" ||
            fieldName.toLowerCase() === "shape__area" ||
            fieldName.toLowerCase() === "shape.starea()") {
            anyValue = Util.round(anyValue, 2);
         }

         suggest = anyValue.toString();
         if (props.toMixedCase) {
            suggest = suggest.ToProperCase();
         }

         outValue = AdvancedSearch.layerName + "|" + fieldName + "|" + suggest;
         words = highlightWords(inputValue, suggest);

         newOptions.Add({ label: suggest, id: ii, words: words, outValue: outValue });
      }

      setOptions(newOptions);
   }

   //Query task failed.
   function queryTask_failed(error: Error) {
      Util.errMsg("AutoComplete - QueryTask failed", error);
   }

   //Input change event.
   function handleInputChange(newInputValue: string) {
      //Util.errMsg("Inputing: " + startValue + " " + newInputValue);

      if (newInputValue && (startValue !== newInputValue)) {
         enableEnterKey = false;
      } else {
         enableEnterKey = true;
      }
      currentValue = { label: newInputValue, id: -1 };
      setInputValue(newInputValue);
      //AdvancedSearch.searchText = newInputValue;
   }

   //Highlight change event.
   function handleHighlightChange(option: iOption | null, reason: string) {
      if (reason === "keyboard") {
         if (option && option.label) {
            //Util.errMsg("Highlighting: " + option.label + " Reason: " + reason);
            currentValue = { label: option.label, id: option.id, words: option.words, outValue: option.outValue };
         }
      }
   }

   //Selected value changed event.
   function handleChange(event: React.SyntheticEvent, newValue: iOption | null, reason: string) {
      setOptions(newValue ? [newValue, ...options] : options);
      setValue(newValue);

      if (newValue) {
         //Util.errMsg("SSSSSSS Selected Value: " + newValue.label, newValue);
         let selectedValue: string;

         if (newValue.label) {
            selectedValue = newValue.label;

         } else if (inputValue) {
            selectedValue = inputValue;
         }

         if (reason === "selectOption") {
            props.onSelected(selectedValue, true);

         } else { // User hit enter key on textbox, not the popup list.
            props.onSelected(selectedValue, false);
         }
      }
   }

   //Suggest popup menu open event.
   function handleOpen() {
      enableEnterKey = true;
   }

   //Tab key event.
   function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
      //Util.errMsg("TTTTTTTT Tab Value: " + enableEnterKey + " " + currentValue.id);

      if (event.key === "Tab" || (enableEnterKey && event.key === "Enter" && currentValue.id === -1)) {
         if (currentValue.label.trim() !== "~^~") {
            let selectedValue: string;

            //Not an exact search.
            if (currentValue.id === -1) {
               selectedValue = currentValue.label;
               props.onSelected(selectedValue, false);

            } else { //Exact search.
               setValue({ label: currentValue.label, id: currentValue.id }); //Sets the input textbox value and options list.
               selectedValue = currentValue.label;
               props.onSelected(selectedValue, true);
            }
         }
      }
   }

   // autoComplete - The completion string appears inline after the input cursor.
   // filterOptions - Required for search by character. filterOptions = {(x) => x}
   // freeSolo - Required for search by character.
   // getOptionLabel - Sets the text value for the suggestion(s) list.
   // inputValue - The users input text.
   // isOptionEqualToValue - Compares suggestion text to input value text.
   // loading = True means component is in the loading state.
   // openOnFocus - True means the suggestion(s) will open on focus.
   // options - Contains the suggestion(s) list.
   // renderInput - The HTML for the component.
   // size - 'small', 'medium'.
   // value - The selected value.

   // open - True means the dropdown suggestion(s) popup is visible.
   // onOpen - The function called when the suggestion(s) popup requests to be opened.
   // onClose - The function called when the suggestion(s) popup requests to be closed.
   // onChange - Fired when a suggestion is selected.

   //Util.errMsg("Bottom of AutoComplete Render before return: " + props.initialValue);

   //Get render option.
   function getRenderOption(option: iOption): JSX.Element[] {
      const jsx: JSX.Element[] = [];

      if (option && option.words) {
         for (const word of option.words) {
            if (word.type === "Bold") {
               jsx.push(
                  <span key={Util.uid()} style={{ fontWeight: 600 }}>{word.value}</span>,
               );
            } else if (word.type === "LayerName") {
               jsx.push(
                  <span key={Util.uid()} style={{ color: '#9F3333' }}><i>{word.value}</i></span>,
               );
            } else if (word.type === "AliasName") {
               jsx.push(
                  <span key={Util.uid()}>{word.value}</span>,
               );
            } else {
               jsx.push(
                  <span key={Util.uid()}>{word.value}</span>,
               );
            }
         }
      }

      return (jsx);
   }

   //Set popup menu width.
   const customPopper = function (props3) {
      return (<Popper {...props3} style={{ width: Settings.leftPanelWidth }}></Popper>);
   };

   return (
      <Autocomplete
         sx={{ width: props.width, bgcolor: "white" }}
         filterOptions={(x) => x}
         options={options}
         autoComplete
         includeInputInList
         value={value}
         freeSolo={true}
         PopperComponent={customPopper}

         onInputChange={(event, newInputValue) => {
            handleInputChange(newInputValue);
         }}

         onHighlightChange={(event, option, reason) => {
            handleHighlightChange(option, reason);
         }}

         onChange={(event, newValue, reason) => {
            handleChange(event, newValue, reason);
         }}

         onKeyDown={(event) => {
            handleKeyDown(event);
         }}

         onOpen={(event) => {
            handleOpen();
         }}

         renderInput={(params) => (
            <TextField
               {...params}
               label="Search"
               title={props.tooltip}
               placeholder={props.children}
               autoFocus={props.focusMe}
               autoComplete="off"
            />
         )}

         renderOption={(props2, option) => {
            return (
               <li {...props2} style={{ fontSize: "0.95rem" }}>
                  <div>
                     {getRenderOption(option)}
                  </div>
               </li>
            );
         }}

      />
   ); //end return.
} //end function.

//Build array of words to be highlighted.
function highlightWords(searchText: string, suggest: string): iWord[] {

   //Initialize locals.
   const words: iWord[] = [];
   const maxSearchText: number = 100;
   let j: number;
   let k: number;
   let pos: number;
   let txt: string;
   let word: string;
   let reg: any;
   let matchText: string;
   let matchFound: boolean;
   const matchArray: boolean[] = [];
   let highlighting: boolean = false;
   let normalTexting: boolean = false;
   let myRun: string = "";

   //Get the search text and split into words.
   const textBoxWords: string[] = Util_App.cleanAndSplit(searchText, 15);

   //Clear all character positions that match users text.
   const numChar: number = Math.min(maxSearchText, suggest.length);
   for (j = 0; j < numChar; j++) {
      matchArray[j] = false;
   }

   //Starts with a number, so highlight starting characters only.
   matchFound = false;

   if (startsWith) {
      if (suggest.toLowerCase().StartsWith(searchText.toLowerCase())) {
         matchFound = true;

         for (j = 0; j < Math.min(searchText.length, maxSearchText); j++) {
            matchArray[j] = true;
         }
      }

   } else { //Highlight all words that match users search text.
      for (word of textBoxWords) {
         pos = suggest.toLowerCase().indexOf(word.toLowerCase());
         if (pos !== -1) {
            matchText = "";
            for (j = 0; j < word.length; j++) {
               matchText += "~";
            }
            reg = new RegExp(word.toLowerCase(), "g");
            txt = suggest.toLowerCase().replace(reg, matchText);

            matchFound = true;
            j = 0;

            for (k = 0; k < txt.length; k++) {
               if (txt.charAt(k) === "~") {
                  if (j < maxSearchText - 1) {
                     matchArray[j] = true;
                  }
               }
               j += 1;
            }
         }
      }
   }

   //Build runs.
   if (matchFound) {
      for (j = 0; j < Math.min(suggest.length, maxSearchText); j++) {

         //Character matches.
         if (matchArray[j]) {
            if (normalTexting) {
               normalTexting = false;
               words.Add({ value: myRun, type: "Bold" });
            }

            if (!highlighting) {
               myRun = ""; //Start new run.
               highlighting = true;
            }

         } else { //Character does not match.
            if (highlighting) {
               highlighting = false;
               words.Add({ value: myRun, type: "Normal" });
            }

            if (!normalTexting) {
               myRun = ""; //Start new run.
               normalTexting = true;
            }
         }
         myRun += suggest.SubStr(j, 1);
      }

      //Add last run.
      if (myRun !== "") {
         if (highlighting) {
            words.Add({ value: myRun, type: "Normal" });
         } else {
            words.Add({ value: myRun, type: "Bold" });
         }
      }
      highlighting = false;
      normalTexting = false;

   } else { //Nothing to highlight.
      words.Add({ value: suggest, type: "Bold" });
   }

   return words;
}
